import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import "./Header.css";
import { ReactComponent as ClaryNextLogo } from "../images/Logo.svg";
import cookies from "js-cookie";
import {
  CircleDollarSign,
  CircleUserRound,
  LayoutDashboard,
  LogOut,
  ShoppingCart,
  ReceiptText,
  Mail,
  BookOpen,
  Orbit,
  Shield,
  Beaker,
  MessageSquare,
  Settings,
  Menu,
  Sparkles,
  UserCog,
} from "lucide-react";
import { useSpaces } from "../context/SpaceProvider";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "./ui/dropdown-menu";
import { config } from "../Environment";
import { WSContext } from "../App";
import { useEffectOnce } from "../hooks/useEffectOnce";
import { distributionService } from "../services/distributionService";
import MessageBox from "./MessageBox";
import Localize, { LocalizeSubMenu } from "./Localize";
import useRetryFetch from "../hooks/useRetryFetch";
import { tryGetUserProfile, stopImpersonating } from "../api/auth";
import { SYSADMIN_ROLE, VENDOR_ROLE, TESTER_ROLE } from "../lib/roles";
import TesterBadge from "./TesterBadge";

import LoggerContext from "../context/LoggerProvider";
import PresListContext from "../context/PresListProvider";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { redirectOnLogout } from "../lib/logout";
import { useBranding } from "../branding/useBranding";
import GeneralChat from "./chat/generalChat";
import { NotificationBell } from "./notifications/NotificationBell";
import { InsightIndicator } from "./companion/InsightIndicator";
import MobileNavDrawer from "./MobileNavDrawer";
import { useIsMobile } from "../hooks/use-mobile";
import { useAiCredits, openBuyAiCredits, BuyAiCreditsHost } from "./pwf/studio/AiCredits";

// AI-credit balance row for the account dropdown. Lives in its own component
// so the balance query only runs while the (authenticated) menu is open.
const AiCreditsMenuItem = () => {
  const { t } = useTranslation();
  const credits = useAiCredits();
  return (
    <DropdownMenuItem onClick={openBuyAiCredits} title={t("get_ai_credits", "Get AI credits")}>
      <Sparkles className="mr-2 h-4 w-4" />
      <span className="flex-1">{t("ai_credits", "AI credits")}</span>
      <span className="ms-3 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5">
        {credits ?? "…"}
      </span>
    </DropdownMenuItem>
  );
};

function Header() {
  const { auth, setAuth, user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const retryFetch = useRetryFetch();

  const [showMessageBox, setShowMessageBox] = useState(false);
  const [msgBoxTitle, setMsgBoxTitle] = useState("");
  const [msgBoxButtons, setMsgBoxButtons] = useState({});
  const [msgBoxInputs, setMsgBoxInputs] = useState([]);
  const [msgBoxId, setMsgBoxId] = useState("");
  const [msgBoxMoreJSX, setMsgBoxMoreJSX] = useState(null);

  const [chatRequestUser, setChatRequestUser] = useState("");
  const [chatRequestProcessId, setChatRequestProcessId] = useState("");
  const [chatRequestRoomId, setChatRequestRoomId] = useState(0);

  const { hasSpaces, spaces } = useSpaces();

  // Track locally selected space from SpacesView
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setSelectedSpaceId(e.detail);
    };
    window.addEventListener("spaces-selected-change", handler);
    return () => window.removeEventListener("spaces-selected-change", handler);
  }, []);

  // Clear when navigating away from /spaces
  useEffect(() => {
    if (location.pathname !== "/spaces") {
      setSelectedSpaceId(null);
    }
  }, [location.pathname]);

  const selectedSpace = spaces.find((s) => s._id === selectedSpaceId) || null;
  const { t } = useTranslation();

  const { socket, roomId, setRoomId, globalAbortController, setListRefresh } =
    useContext(WSContext);

  const { Logger } = useContext(LoggerContext);

  const handleStopImpersonating = async () => {
    try {
      const responseBody = await stopImpersonating(retryFetch);
      if (responseBody?.accessToken) {
        const newAuth = {
          email: responseBody.email,
          accessToken: responseBody.accessToken,
          impersonatedBy: undefined,
        };

        // Write to localStorage synchronously so useRetryFetch (which reads
        // the access token from localStorage, not React state) picks up the
        // new token immediately — setAuth's persistence runs in a useEffect
        // and would otherwise still be using the impersonated user's token.
        localStorage.setItem("auth", JSON.stringify(newAuth));
        setAuth(newAuth);

        const userProfile = await tryGetUserProfile(retryFetch);
        if (userProfile) {
          userProfile.roles ??= [];
          setUser(userProfile);
        }

        socket?.send(
          JSON.stringify({
            command: "login",
            payload: { email: responseBody.email },
          })
        );

        queryClient.clear();
        setListRefresh(true);
        navigate(`/portal`);
      }
    } catch (err) {
      Logger.error("Failed to stop impersonating " + err);
    }
  };

  const {
    setContextPrescriptions,
    setPublicPrescriptions,
    setProcesses,
    setExpertProcesses,
  } = useContext(PresListContext);

  const { branding } = useBranding();

  function isUser() {
    const email = auth?.email;
    const accessToken = auth?.accessToken;
    return !!email && !!accessToken;
  }

  function isAdmin() {
    const roles = user.roles ?? [];
    return roles.includes(SYSADMIN_ROLE);
  }

  function isVendor() {
    const roles = user.roles ?? [];
    return roles.includes(VENDOR_ROLE);
  }

  function isTester() {
    const roles = user.roles ?? [];
    return roles.includes(TESTER_ROLE);
  }

  function isAdminPage() {
    return (
      location.pathname === "/admin" ||
      location.pathname?.startsWith("/users") ||
      location.pathname === "/setup"
    );
  }

  function isDashboardPage() {
    return location.pathname === "/portal";
  }
  function isSetupPage() {
    return location.pathname === "/setup";
  }

  // for admins who are in the admin area
  function showDashboardButton() {
    return isAdmin() && isAdminPage();
  }
  function showSetupButton() {
    return isAdmin() && isAdminPage() && !isSetupPage();
  }

  // for admins who are in the dashboard area
  function showAdminButton() {
    return isAdmin();
  }

  function showLoginButton() {
    return !isUser();
  }

  function showUserMenu() {
    return isUser();
  }

  const openMessageBox = (title, id, buttons, inputs, moreJSX) => {
    setMsgBoxTitle(title);
    setMsgBoxId(id);
    setMsgBoxButtons(buttons);
    setMsgBoxInputs(inputs);
    setMsgBoxMoreJSX(moreJSX);
    setShowMessageBox(true);
  };

  const onButton = async (result) => {
    Logger.debug("MessageBox button was clicked");
    Logger.debug(result);

    if (result.btnId === "yes") {
      // chat was accepted, change to ShowFullLog, join room and notify user
      socket.send(
        JSON.stringify({
          command: "acceptchat",
          payload: { email: chatRequestUser, roomId: chatRequestRoomId },
        })
      );

      // collect necessary process log information
      let response = await retryFetch(
        `${config.API_BASE}/api/processes/${chatRequestProcessId}`
      );
      let process = await response.json();

      let stateToPass = {
        processId: chatRequestProcessId,
        presName: process.prescriptionName,
        presHistoryId: process.fiPrescriptionHistory._id,
        autochat: true,
      };
      if (auth?.email && process.fiExpert?.email === auth.email) {
        stateToPass.userEmail = process.fiInitiator?.email;
        navigate("/review", { state: stateToPass });
      } else {
        stateToPass.expertEmail = process.fiExpert?.email;
        navigate("/review", { state: stateToPass });
      }
    } else {
      // chat was refused, send message to requesting user
      socket.send(
        JSON.stringify({
          command: "refusechat",
          payload: {
            email: chatRequestUser,
            roomId: chatRequestRoomId,
          },
        })
      );
    }
  };

  // Check cookie only after auth is potentially loaded
  useEffect(() => {
    // Only check cookie if user appears to be logged in
    if (auth?.email && auth?.accessToken) {
      checkCookie();
    }
  }, [auth?.email, auth?.accessToken]);

  useEffectOnce(() => {
    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe(async (msg) => {
        Logger.debug("Header incoming message");

        switch (msg.command) {
          case "requestchat":
            setChatRequestUser(msg.payload.email);
            setChatRequestProcessId(msg.payload.processId);
            setChatRequestRoomId(msg.payload.roomId);

            // get process information
            let response = await retryFetch(
              `${config.API_BASE}/api/processes/${msg.payload.processId}`
            );
            let process = await response.json();

            openMessageBox(
              t("msg_chat_requested", {
                user: msg.payload.email,
              }),
              "requestchat",
              [
                { id: "yes", text: t("yes") },
                { id: "no", text: t("no") },
              ],
              [],
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{t("activity_name")}</th>
                      <th>{t("status")}</th>
                      <th>{t("last_update")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{process.prescriptionName}</td>
                      <td>{t(process.status)}</td>
                      <td>{showLocale(process.lastUpdated)}</td>
                    </tr>
                  </tbody>
                </table>
                <p></p>
              </>
            );
            break;
          default:
            <></>;
        }
      });

    return () => {
      // cleanup subscription
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  const showLocale = (ts) => {
    return new Date(ts).toLocaleString(
      cookies.get("i18next")?.split("-")[0] || "en"
    );
  };

  const navigateToAdmin = () => {
    navigate("/admin");
  };
  const navigateToSetup = () => {
    navigate("/setup");
  };

  const navigateToPortal = () => {
    navigate("/portal");
  };

  const navigateToHome = () => {
    navigate("/home");
  };

  const login = () => {
    navigate("/login", {
      state: { from: location, acceptId: location.state?.acceptId },
    });
  };

  const logout = async () => {
    // Close room first
    if (roomId && socket && socket.readyState === WebSocket.OPEN) {
      Logger.info("Message LEAVEROOM has been sent!");
      socket.send(
        JSON.stringify({
          command: "leaveroom",
          payload: undefined,
        })
      );
      socket.send(
        JSON.stringify({
          command: "changeuserstate",
          payload: "online",
        })
      );
      Logger.debug("HEADER: ROOM ID SET TO 0");
      setRoomId(0);
    }

    // abort with globalAbortController (mainly for loading prescription lists)
    sessionStorage.setItem("semaphore", "false");
    globalAbortController.abort();

    let response = await fetch(config.API_BASE + "/api/auth/logout", {
      credentials: "include",
    });
    Logger.debug(`logout response status ${response.status}`);

    if (!response.ok) alert(t("msg_error_logout"));

    if (response.status == 200) {
      try {
        let result = await response.json();
        Logger.debug(result);
      } catch (err) {
        Logger.debug(`failed to parse logout response`, err);
      }
    }
    setAuth({});
    queryClient.clear();
    localStorage.removeItem("clary_view_mode");

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          command: "logout",
          payload: undefined,
        })
      );
    }

    // get rid of cache
    setContextPrescriptions(null);
    setPublicPrescriptions(null);
    setProcesses(null);
    setExpertProcesses(null);

    navigate(redirectOnLogout({ location }));
  };

  // const register = () => {
  //   navigate("/register");
  // };

  const checkCookie = async () => {
    // Don't check cookie if user is not logged in
    if (!auth?.email || !auth?.accessToken) {
      Logger.debug("Skipping cookie check - user not logged in");
      return;
    }

    Logger.debug("Checking cookie from...", location);
    try {
      let response = await fetch(`${config.API_BASE}/api/auth/checkcookie`, {
        credentials: "include",
      });

      // Check if the response has valid content type
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        Logger.warn("checkCookie: Invalid content-type, ignoring");
        return; // Don't clear auth on network errors
      }

      let responseObj = await response.json();
      let jwtState = responseObj.result;

      if (!jwtState) {
        // Cookie might be expired on server side, but don't force logout
        // The token refresh mechanism will handle expired tokens properly
        Logger.warn("Cookie check returned false - token refresh will handle if truly expired");
      } else {
        Logger.debug("Cookie still valid.");
      }
    } catch (error) {
      Logger.warn("checkCookie error (not clearing auth):", error);
      // Network errors should NOT clear auth - user might be offline temporarily
    }
  };

  function renderProfileIconUsername() {
    if (!user?.email) {
      return (<></>);
    }
    let displayName = user.displayName;
    if (!displayName) {
      displayName = [user.firstName, user.lastName].join(" ");
    }
    if (!displayName) {
      displayName = "";
    }
    return (
      <div className="px-2 py-1.5">
        {displayName && <p className="text-sm font-medium">{displayName}</p>}
        <p className="text-xs text-muted-foreground">{user.email || "N/A"}</p>
      </div>
    );
  }

  function navigateToMyProfile() {
    navigate("/profile");
  }
  function navigateToMyAccount() {
    navigate("/account");
  }

  function navigateToDashboard() {
    navigate("/portal");
  }
  function navigateToCommerce() {
    navigate("/commerce");
  }

  function navigateToMarketplace() {
    navigate("/marketplace");
  }

  function navigateToExamples() {
    navigate("/examples");
  }

  function navigateToContactUs() {
    navigate("/contact");
  }
  /*
            <Button onClick={logout} variant="outline">
              {t("logout")}
            </Button>
  */
  function renderCommerceMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToCommerce}>
        <CircleDollarSign className="mr-2 h-4 w-4" />
        <span>{t("vendor_management", "Vendor Management")}</span>
      </DropdownMenuItem>
    );
  }

  function renderMarketplaceMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToMarketplace}>
        <ShoppingCart className="mr-2 h-4 w-4" />
        <span>{t("marketplace")}</span>
      </DropdownMenuItem>
    );
  }

  function renderExamplesMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToExamples}>
        <BookOpen className="mr-2 h-4 w-4" />
        <span>{t("examples")}</span>
      </DropdownMenuItem>
    );
  }

  function renderMyAccountMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToMyAccount}>
        <CircleUserRound className="mr-2 h-4 w-4" />
        <span>{t("my_account")}</span>
      </DropdownMenuItem>
    );
  }

  function renderDashboardMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToDashboard}>
        <LayoutDashboard className="mr-2 h-4 w-4" />
        <span>{t("dashboard")}</span>
      </DropdownMenuItem>
    );
  }

  function renderContactUsMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToContactUs}>
        <Mail className="mr-2 h-4 w-4" />
        <span>{t("contact_us")}</span>
      </DropdownMenuItem>
    );
  }

  function renderLogoutMenuItem() {
    return (
      <DropdownMenuItem onClick={logout}>
        <LogOut className="mr-2 h-4 w-4" />
        <span>{t("logout")}</span>
      </DropdownMenuItem>
    );
  }

  function hasAnyRole() {
    const roles = user?.roles ?? [];
    return roles.length > 0;
  }

  function renderAdminMenuItem() {
    return (
      <DropdownMenuItem onClick={navigateToAdmin}>
        <Shield className="mr-2 h-4 w-4" />
        <span>{t("link_admin_area")}</span>
      </DropdownMenuItem>
    );
  }

  function renderMenuItems() {
    return (
      <>
        {renderProfileIconUsername()}
        {/* Tester badge with full details in menu */}
        {isTester() && (
          <div className="px-2 pb-1">
            <TesterBadge
              expiresAt={user.testerExpiresAt}
              size="sm"
              variant="full"
            />
          </div>
        )}
        <DropdownMenuSeparator />
        {renderMyAccountMenuItem()}
        <AiCreditsMenuItem />
        {isVendor() && renderCommerceMenuItem()}
        {showAdminButton() && renderAdminMenuItem()}
        <DropdownMenuSeparator />
        {renderContactUsMenuItem()}
        <LocalizeSubMenu />
        <DropdownMenuSeparator />
        {renderLogoutMenuItem()}
      </>
    );
  }

  // Mobile header: hamburger + centered logo + notification bell
  if (isMobile && isUser()) {
    return (
      <>
        {/* Buy-AI-credits dialog, opened from the mobile drawer's credits row */}
        {auth?.email && <BuyAiCreditsHost />}
        <header className="z-50 sticky w-full top-0 flex h-12 flex-shrink-0 items-center bg-white/80 backdrop-blur-md border-b border-gray-200/60 shadow-sm px-3 justify-between relative">
          {/* Space-color tint when inside a space */}
          {location.pathname === "/spaces" && selectedSpace?.color && (
            <>
              <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                style={{
                  background: `linear-gradient(135deg, ${selectedSpace.color}12, transparent 60%)`,
                }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent, ${selectedSpace.color}40, transparent)`,
                }}
              />
            </>
          )}

          {auth?.impersonatedBy ? (
            <div className="absolute top-1 left-0 right-0 mx-auto z-10 flex w-fit max-w-[92%] items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[0.65rem] font-medium text-amber-800 shadow-sm">
              <UserCog className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {t("currently_impersonating")}: {auth?.email}
              </span>
              <button
                type="button"
                onClick={handleStopImpersonating}
                className="shrink-0 rounded-full bg-amber-200/70 px-2 py-0.5 text-amber-900 hover:bg-amber-300/80 transition-colors"
              >
                {t("stop_impersonating")}
              </button>
            </div>
          ) : (
            ""
          )}

          {/* Left: Hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-muted/50 transition-colors"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5 text-foreground" />
          </button>

          {/* Center: Logo */}
          <Button
            variant="none"
            size="img"
            className="absolute left-1/2 -translate-x-1/2 py-1"
            onClick={navigateToHome}
          >
            <ClaryNextLogo
              className="h-7 w-auto max-w-[120px] text-primary"
              style={{ height: "1.75rem", width: "auto" }}
              alt="Brand logo"
            />
          </Button>

          {/* Right: Notification bell */}
          <div className="flex items-center">
            <NotificationBell />
          </div>
        </header>

        <MobileNavDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          onLogout={logout}
        />

        {showMessageBox ? (
          <MessageBox
            onButton={onButton}
            buttons={msgBoxButtons}
            text={msgBoxTitle}
            inputs={msgBoxInputs}
            id={msgBoxId}
            moreJSX={msgBoxMoreJSX}
            onClose={() => setShowMessageBox(false)}
          />
        ) : (
          ""
        )}
      </>
    );
  }

  // Desktop / unauthenticated mobile header (unchanged)
  return (
    <>
      {/* Buy-AI-credits dialog, opened from the account menu's credits row */}
      {auth?.email && <BuyAiCreditsHost />}
      <header className="z-50 sticky w-full top-0 flex h-12 md:h-16 flex-shrink-0 items-center bg-white/80 backdrop-blur-md gap-4 border-b border-gray-200/60 shadow-sm px-2 md:px-6 justify-between relative">
        {/* Space-color tint when inside a space */}
        {location.pathname === "/spaces" && selectedSpace?.color && (
          <>
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-500"
              style={{
                background: `linear-gradient(135deg, ${selectedSpace.color}12, transparent 60%)`,
              }}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-px"
              style={{
                background: `linear-gradient(90deg, transparent, ${selectedSpace.color}40, transparent)`,
              }}
            />
          </>
        )}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1.5">
            <Button
              variant="none"
              size="img"
              className="py-1"
              onClick={navigateToHome}
            >
              <ClaryNextLogo
                className="h-8 w-auto max-w-[160px] text-primary"
                style={{ height: "2rem", width: "auto" }}
                alt="Brand logo"
              />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  beta
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-0">
                <div className="p-4">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center flex-shrink-0 shadow-md">
                      <Beaker className="h-4 w-4 text-white" />
                    </div>
                    <h4 className="font-bold text-sm text-gray-900">{t('beta_banner_title')}</h4>
                  </div>
                  <p className="text-xs text-gray-700 mb-3 leading-relaxed">
                    {t('beta_banner_description')}
                  </p>
                  <Button
                    onClick={() => navigate("/contact")}
                    size="sm"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md text-xs"
                  >
                    <MessageSquare className="h-3 w-3 mr-2" />
                    {t('beta_banner_feedback')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

        </div>

        {auth?.impersonatedBy ? (
          <div className="absolute top-1.5 left-0 right-0 mx-auto z-10 flex w-fit max-w-[60%] items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 shadow-sm">
            <UserCog className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {t("currently_impersonating")}: {auth?.email}
            </span>
            <button
              type="button"
              onClick={handleStopImpersonating}
              className="shrink-0 rounded-full bg-amber-200/70 px-2.5 py-0.5 text-amber-900 hover:bg-amber-300/80 transition-colors"
            >
              {t("stop_impersonating")}
            </button>
          </div>
        ) : (
          ""
        )}
        <div className="flex flex-row items-center gap-2 md:gap-4">
          {showLoginButton() ? (
            <div className="flex items-center gap-2">
              <Localize />
              <Button variant="ghost" onClick={navigateToContactUs} className="text-muted-foreground hover:text-foreground hidden sm:inline-flex">
                {t("contact_us")}
              </Button>
              <Button onClick={login}>{t("login")}</Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1">
                {/*<InsightIndicator />*/}
                <NotificationBell />
                {/*<GeneralChat />*/}
              </div>
              <div className="hidden md:block w-px h-6 bg-gray-200" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden md:block h-7 w-7 rounded-full overflow-hidden ring-2 ring-transparent hover:ring-primary/30 transition-all cursor-pointer">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                        user?.displayName || user?.firstName || user?.email?.split("@")[0] || "U"
                      )}&background=eef2ff&color=4f46e5&bold=true&size=36`}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {renderMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </header>
      {showMessageBox ? (
        <MessageBox
          onButton={onButton}
          buttons={msgBoxButtons}
          text={msgBoxTitle}
          inputs={msgBoxInputs}
          id={msgBoxId}
          moreJSX={msgBoxMoreJSX}
          onClose={() => setShowMessageBox(false)}
        />
      ) : (
        ""
      )}
    </>
  );
}

export default Header;
